// AdhdScreeningWLC - Main Orchestrator
// ======================================
// Public-facing ADHD referral initiation form for Well Life Clinic (WLC).
// Guides prospective patients through an NHS Right-to-Choose flow:
//
//   1. Welcome carousel  (3 steps - RTC context)
//   2. ASRS Part A       (6 screening questions)
//   3. Break screen      (threshold gate - ≥ 4/6 continues, < 4/6 ends early)
//   4. Impairment scale  (6 functional-impact Likert questions)
//   5. Personal details  (6 screens - name, DOB, NHS#, address, phone, email)
//   6. Previous history  (3 yes/no questions)
//   7. Completion screen (consent checkbox → submit → email GP letter)
//
// State is persisted to localStorage under WLC-specific keys so progress
// survives page refreshes and doesn't collide with other provider screeners.

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { CheckCircleIcon } from '@heroicons/react/solid';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

import { ALL_QUESTIONS, ASRS_COUNT, IMPAIRMENT_COUNT, PERSONAL_COUNT, PREV_ASSESSMENT_COUNT } from './constants/questions';
import { ASRS_THRESHOLD, calculateASRSPartAScore, meetsASRSThreshold, deriveAsrsGateScreen } from './constants/scoring';
import { STORAGE_KEYS } from './constants/storageKeys';
import { ASRS_RATING_OPTIONS, IMPAIRMENT_LIKERT_OPTIONS, YES_NO_OPTIONS } from './constants/options';
import { FUNNEL_PHASES, getQuestionStepMeta, getWelcomeStepMeta, isStepSkipped } from './constants/funnelSteps';

import WlcDateOfBirthPicker from './components/WlcDateOfBirthPicker';
import WlcPhoneNumberField from './components/WlcPhoneNumberField';
import WlcAddressField from './components/WlcAddressField';
import { fireEventOnce, fireFunnelEvent, getOrCreateSessionId } from './utils/fireEventOnce';

// ─── Brand constants ─────────────────────────────────────────────────────────
const BRAND = { primary: '#43213e', accent: '#e7c22f' };

// ─── CSS keyframes (injected once) ──────────────────────────────────────────
const ANIMATIONS = `
@keyframes fade-up { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
@keyframes fade-in { 0%{opacity:0} 100%{opacity:1} }
@keyframes fade-slide-in { 0%{opacity:0;transform:translateX(-20px)} 100%{opacity:1;transform:translateX(0)} }
@keyframes fade-slide-up { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
`;

export default function AdhdScreeningWLC({ source = 'main' }) {
    const isProd = (import.meta.env.VITE_ENVIRONMENT === 'production' || process.env.NODE_ENV === 'production');

    // ── Core form state ─────────────────────────────────────────────────────
    const { register, handleSubmit, watch, setValue } = useForm();
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [submittedAnswers, setSubmittedAnswers] = useState([]);
    const [isComplete, setIsComplete] = useState(false);

    // ── Welcome carousel state ──────────────────────────────────────────────
    const [welcomeComplete, setWelcomeComplete] = useState(false);
    const [currentWelcomeStep, setCurrentWelcomeStep] = useState(0);

    // ── Break / threshold state ─────────────────────────────────────────────
    const [showScoreMessage, setShowScoreMessage] = useState(false);

    // ── Below-threshold end-early state ─────────────────────────────────────
    const [belowThreshold, setBelowThreshold] = useState(false);

    // ── Hydration gate ──────────────────────────────────────────────────────
    // localStorage restore runs in an effect after the first render (which
    // defaults to welcome step 0 / question 0). Tracking must wait until
    // restore finishes, otherwise every mid-funnel refresh would emit a
    // spurious step_viewed for the wrong screen.
    const [hasHydrated, setHasHydrated] = useState(false);

    // ── Completion / submission state ────────────────────────────────────────
    const [consentGiven, setConsentGiven] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showResultBanner, setShowResultBanner] = useState(false);
    const [resultBannerType, setResultBannerType] = useState('');
    const [resultBannerMessage, setResultBannerMessage] = useState('');

    // ── Extra state for complex input types ─────────────────────────────────
    // Phone: raw (display) + normalised (E.164)
    const [phoneRaw, setPhoneRaw] = useState('');
    const [phoneNormalized, setPhoneNormalized] = useState('');
    // Address: structured object
    const [addressValue, setAddressValue] = useState({ streetAddress: '', city: '', postcode: '' });
    

    // ── Derived helpers ─────────────────────────────────────────────────────
    const allQuestions = useMemo(() => ALL_QUESTIONS, []);
    const currentQuestion = allQuestions[currentQuestionIndex];

    // Progress bar: two segments — ASRS (6 Qs) and everything after (15 Qs).
    // We derive progress from submittedAnswers.length (not currentQuestionIndex)
    // so the bar only advances when a question is actually submitted via Next,
    // not while the user is mid-answer.
    const POST_ASRS_COUNT = IMPAIRMENT_COUNT + PERSONAL_COUNT + PREV_ASSESSMENT_COUNT; // 15
    const answeredCount = submittedAnswers.length;
    const asrsProgress = Math.min(100, (Math.min(answeredCount, ASRS_COUNT) / ASRS_COUNT) * 100);
    const postAsrsAnswered = Math.max(0, answeredCount - ASRS_COUNT);
    const postAsrsProgress = Math.min(100, (postAsrsAnswered / POST_ASRS_COUNT) * 100);

    // Time estimate (roughly 15s per question)
    const remainingQuestions = allQuestions.length - answeredCount;
    const remainingMinutes = Math.max(1, Math.ceil((remainingQuestions * 15) / 60));

    // ── Restore progress from localStorage on mount ─────────────────────────
    useEffect(() => {
        const sq = localStorage.getItem(STORAGE_KEYS.CURRENT_QUESTION);
        const sa = localStorage.getItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
        const sc = localStorage.getItem(STORAGE_KEYS.IS_COMPLETE);
        const sf = localStorage.getItem(STORAGE_KEYS.CURRENT_FORM_DATA);
        const sw = localStorage.getItem(STORAGE_KEYS.WELCOME_COMPLETE);

        let restoredIndex = 0;
        if (sq !== null) {
            const parsed = parseInt(sq, 10);
            // Clamp corrupt / out-of-range values so currentQuestion is always defined.
            restoredIndex = Number.isFinite(parsed)
                ? Math.min(Math.max(parsed, 0), ALL_QUESTIONS.length - 1)
                : 0;
            setCurrentQuestionIndex(restoredIndex);
        }

        let restoredAnswers = [];
        if (sa) {
            try {
                const parsed = JSON.parse(sa);
                if (Array.isArray(parsed)) {
                    restoredAnswers = parsed;
                    setSubmittedAnswers(parsed);
                }
            } catch (_) { /* noop */ }
        }

        const restoredComplete = sc === 'true';
        if (restoredComplete) setIsComplete(true);
        if (sw === 'true') setWelcomeComplete(true);

        if (sf) {
            try {
                const fd = JSON.parse(sf);
                Object.entries(fd).forEach(([k, v]) => setValue(k, v));
            } catch (_) { /* noop */ }
        }

        // Break / below-threshold screens are not persisted. If the user finished
        // ASRS but never advanced past the gate, re-derive which virtual screen
        // to show so a refresh does not strand them on Q6.
        if (!restoredComplete) {
            const gate = deriveAsrsGateScreen(
                restoredAnswers,
                ALL_QUESTIONS,
                restoredIndex,
                ASRS_COUNT,
            );
            if (gate === 'break') setShowScoreMessage(true);
            else if (gate === 'below_threshold') setBelowThreshold(true);
        }

        setHasHydrated(true);
    }, [setValue]);

    // ── Funnel tracking: step_viewed for whichever screen is active ─────────
    // Fires after hydration only. Dedup bag prevents re-fires on refresh/back.
    useEffect(() => {
        if (!hasHydrated) return;

        if (!welcomeComplete) {
            const meta = getWelcomeStepMeta(currentWelcomeStep);
            fireFunnelEvent({
                eventName: 'step_viewed',
                source,
                once: true,
                ...meta,
            });
            return;
        }

        if (showScoreMessage) {
            fireFunnelEvent({
                eventName: 'step_viewed',
                source,
                once: true,
                stepId: 'break_threshold_met',
                phase: FUNNEL_PHASES.BREAK,
            });
            return;
        }

        if (belowThreshold) {
            fireFunnelEvent({
                eventName: 'step_viewed',
                source,
                once: true,
                stepId: 'below_threshold_end',
                phase: FUNNEL_PHASES.BREAK,
            });
            return;
        }

        if (isComplete) {
            fireFunnelEvent({
                eventName: 'step_viewed',
                source,
                once: true,
                stepId: 'consent',
                phase: FUNNEL_PHASES.COMPLETION,
            });
            return;
        }

        const meta = getQuestionStepMeta(currentQuestion, currentQuestionIndex);
        if (!meta) return;
        fireFunnelEvent({
            eventName: 'step_viewed',
            source,
            once: true,
            ...meta,
        });
    }, [
        hasHydrated,
        welcomeComplete,
        currentWelcomeStep,
        showScoreMessage,
        belowThreshold,
        isComplete,
        currentQuestionIndex,
        currentQuestion,
        source,
    ]);

    // Consent checkbox → step_completed so we can see drop-off before submit.
    useEffect(() => {
        if (!hasHydrated || !isComplete || !consentGiven) return;
        fireFunnelEvent({
            eventName: 'step_completed',
            source,
            once: true,
            stepId: 'consent',
            phase: FUNNEL_PHASES.COMPLETION,
        });
    }, [hasHydrated, isComplete, consentGiven, source]);

    // ── Restore current question's answer when navigating ───────────────────
    useEffect(() => {
        if (!currentQuestion) return;
        const prev = submittedAnswers.find(a => a.questionIndex === currentQuestionIndex);
        if (prev) {
            if (currentQuestion.type === 'names' && typeof prev.answer === 'object') {
                currentQuestion.fields?.forEach(f => setValue(f.id, prev.answer[f.id] || ''));
            } else if (currentQuestion.type === 'phone') {
                setPhoneRaw(prev.answer?.raw || '');
                setPhoneNormalized(prev.answer?.normalized || '');
            } else if (currentQuestion.type === 'address') {
                setAddressValue(prev.answer || { streetAddress: '', city: '', postcode: '' });
            } else if (currentQuestion.type === 'emailConfirm') {
                setValue('email', prev.answer?.email || '');
                setValue('confirmEmail', prev.answer?.confirmEmail || '');
            } else if (currentQuestion.type === 'yesNo' && prev.answer && typeof prev.answer === 'object') {
                setValue(currentQuestion.id, prev.answer.value || '');
                if (prev.answer.details) setValue('medicationDetails', prev.answer.details);
            } else {
                setValue(currentQuestion.id, prev.answer || '');
            }
        } else {
            // Clear fields for unanswered questions
            if (currentQuestion.type === 'names') {
                currentQuestion.fields?.forEach(f => setValue(f.id, ''));
            } else if (currentQuestion.type === 'emailConfirm') {
                setValue('email', ''); setValue('confirmEmail', '');
            } else if (currentQuestion.type === 'phone') {
                setPhoneRaw(''); setPhoneNormalized('');
            } else if (currentQuestion.type === 'address') {
                setAddressValue({ streetAddress: '', city: '', postcode: '' });
            } else {
                setValue(currentQuestion.id, '');
            }
        }
    }, [currentQuestionIndex, submittedAnswers, currentQuestion, setValue]);

    // ── Persist state changes to localStorage ───────────────────────────────
    useEffect(() => { localStorage.setItem(STORAGE_KEYS.CURRENT_QUESTION, currentQuestionIndex.toString()); }, [currentQuestionIndex]);
    useEffect(() => { localStorage.setItem(STORAGE_KEYS.SUBMITTED_ANSWERS, JSON.stringify(submittedAnswers)); }, [submittedAnswers]);
    useEffect(() => { localStorage.setItem(STORAGE_KEYS.IS_COMPLETE, isComplete.toString()); }, [isComplete]);

    // ── Clear all stored data (dev/QA helper) ───────────────────────────────
    const clearStoredData = useCallback(() => {
        Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
        setCurrentQuestionIndex(0);
        setSubmittedAnswers([]);
        setIsComplete(false);
        setWelcomeComplete(false);
        setCurrentWelcomeStep(0);
        setShowScoreMessage(false);
        setBelowThreshold(false);
        setConsentGiven(false);
        setIsSubmitting(false);
        setShowResultBanner(false);
        setPhoneRaw(''); setPhoneNormalized('');
        setAddressValue({ streetAddress: '', city: '', postcode: '' });
        const watchedValues = watch();
        Object.keys(watchedValues).forEach(k => setValue(k, ''));
    }, [watch, setValue]);

    // ── Extract the answer value from the current form state ────────────────
    const extractAnswerValue = useCallback((data) => {
        const q = currentQuestion;
        if (q.type === 'names') {
            return { firstName: (data.firstName || '').trim(), middleName: (data.middleName || '').trim(), lastName: (data.lastName || '').trim() };
        }
        if (q.type === 'emailConfirm') {
            return { email: (data.email || '').trim(), confirmEmail: (data.confirmEmail || '').trim() };
        }
        if (q.type === 'phone') {
            return { raw: phoneRaw, normalized: phoneNormalized };
        }
        if (q.type === 'address') {
            return addressValue;
        }
        if (q.type === 'yesNo' && q.conditionalInput) {
            return { value: data[q.id], details: data[q.id] === 'Yes' ? (data.medicationDetails || '').trim() : '' };
        }
        return data[q.id];
    }, [currentQuestion, phoneRaw, phoneNormalized, addressValue]);

    // ── Validate before allowing "Next" ─────────────────────────────────────
    const validateCurrentQuestion = useCallback((data) => {
        const q = currentQuestion;
        if (q.type === 'names') {
            if (!(data.firstName || '').trim() || !(data.lastName || '').trim()) return 'First and last name are required.';
        }
        if (q.type === 'emailConfirm') {
            const e = (data.email || '').trim();
            const c = (data.confirmEmail || '').trim();
            if (!e) return 'Email is required.';
            if (!/^\S+@\S+\.\S+$/.test(e)) return 'Please enter a valid email address.';
            if (e.toLowerCase() !== c.toLowerCase()) return 'Email addresses do not match.';
        }
        if (q.type === 'phone' && !phoneNormalized) return 'Please enter a valid phone number.';
        if (q.type === 'address') {
            if (!addressValue.streetAddress?.trim()) return 'Street address is required.';
            if (!addressValue.city?.trim()) return 'City or town is required.';
            if (!addressValue.postcode?.trim()) return 'Postcode is required.';
        }
        if (q.type === 'yesNo' && q.required && !data[q.id]) return 'Please select Yes or No.';
        if (q.type === 'yesNo' && q.conditionalInput && data[q.id] === 'Yes' && q.conditionalInput.required && !(data.medicationDetails || '').trim()) {
            return 'Please provide details.';
        }
        if ((q.type === 'rating' || q.type === 'likert') && q.required && !data[q.id]) return 'Please select an option.';
        if (q.type === 'dob' && q.required && !data.dob) return 'Please select your date of birth.';
        // NHS number is optional - no validation needed beyond format hint
        return null;
    }, [currentQuestion, phoneNormalized, addressValue]);

    // ── Submit handler for each question ────────────────────────────────────
    const [inlineError, setInlineError] = useState('');

    const onSubmit = useCallback((data) => {
        const err = validateCurrentQuestion(data);
        if (err) { setInlineError(err); return; }
        setInlineError('');

        const answerValue = extractAnswerValue(data);
        const newAnswer = { question: currentQuestion.question, answer: answerValue, questionIndex: currentQuestionIndex };

        const updated = [...submittedAnswers];
        const idx = updated.findIndex(a => a.questionIndex === currentQuestionIndex);
        if (idx >= 0) updated[idx] = newAnswer; else updated.push(newAnswer);
        setSubmittedAnswers(updated);

        // Question answered / advanced — fire before threshold / navigation so
        // the journey always records completion of this step even if the user
        // screens out immediately after.
        const stepMeta = getQuestionStepMeta(currentQuestion, currentQuestionIndex);
        if (stepMeta) {
            fireFunnelEvent({
                eventName: 'step_completed',
                source,
                once: true,
                ...stepMeta,
                // Analytics only, never written to Firestore: an optional question
                // advanced with nothing entered is a skip, which is a different
                // thing from an abandonment and cannot be told apart afterwards.
                skipped: isStepSkipped(currentQuestion, answerValue),
            });
        }

        // Clear form fields for the next screen
        Object.keys(data).forEach(k => setValue(k, ''));
        localStorage.removeItem(STORAGE_KEYS.CURRENT_FORM_DATA);

        // After 6th ASRS question (index 5): show the break/threshold screen
        if (currentQuestionIndex === ASRS_COUNT - 1) {
            const asrsForScoring = updated
                .filter(a => { const q = allQuestions[a.questionIndex]; return q && q.phase === 'asrs'; })
                .map(a => ({ questionId: allQuestions[a.questionIndex].id, answer: a.answer }));
            const score = calculateASRSPartAScore(asrsForScoring);

            // All 6 screening questions answered -- log regardless of threshold outcome.
            // The score is computed first so the analytics mirror can carry the
            // outcome and the threshold decision; the Firestore event written by
            // this call is byte-for-byte what it was before.
            fireFunnelEvent({
                eventName: 'screening_complete',
                source,
                once: true,
                storageKey: STORAGE_KEYS.SCREENING_COMPLETE_LOGGED,
                scoring: {
                    partAScore: score,
                    partAMax: ASRS_COUNT,
                    threshold: ASRS_THRESHOLD,
                    meetsThreshold: meetsASRSThreshold(score),
                },
            });

            if (meetsASRSThreshold(score)) {
                setShowScoreMessage(true);
            } else {
                // User screened out -- intentional funnel exit, distinct from abandonment
                fireEventOnce('threshold_not_met', source, STORAGE_KEYS.THRESHOLD_NOT_MET_LOGGED);
                setBelowThreshold(true);
            }
            return;
        }

        // Advance to next question or mark complete
        if (currentQuestionIndex < allQuestions.length - 1) {
            const nextIndex = currentQuestionIndex + 1;

            // Transitioning from impairment (last index = ASRS_COUNT + IMPAIRMENT_COUNT - 1)
            // to personal details (first index = ASRS_COUNT + IMPAIRMENT_COUNT) -- the PII barrier
            if (nextIndex === ASRS_COUNT + IMPAIRMENT_COUNT) {
                fireEventOnce('personal_details_started', source, STORAGE_KEYS.PERSONAL_STARTED_LOGGED);
            }

            setCurrentQuestionIndex(nextIndex);
        } else {
            setIsComplete(true);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentQuestionIndex, submittedAnswers, currentQuestion, allQuestions, extractAnswerValue, validateCurrentQuestion, setValue, source]);

    // ── Navigation helpers ──────────────────────────────────────────────────
    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setInlineError('');
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        } else {
            setWelcomeComplete(false);
            setCurrentWelcomeStep(2);
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleScoreMessageContinue = () => {
        fireFunnelEvent({
            eventName: 'step_completed',
            source,
            once: true,
            stepId: 'break_threshold_met',
            phase: FUNNEL_PHASES.BREAK,
        });
        setShowScoreMessage(false);
        setCurrentQuestionIndex(ASRS_COUNT);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // ── Final submission ────────────────────────────────────────────────────
    const handleFinalSubmit = async () => {
        if (isSubmitting || !consentGiven) return;
        setIsSubmitting(true);
        setShowResultBanner(false);

        // Retries matter for the submit funnel — do not dedupe these events.
        fireFunnelEvent({
            eventName: 'submission_attempt',
            source,
            once: false,
        });

        try {
            // Build structured payload from submitted answers
            const answersByPhase = {};
            for (const a of submittedAnswers) {
                const q = allQuestions[a.questionIndex];
                if (!q) continue;
                if (!answersByPhase[q.phase]) answersByPhase[q.phase] = {};
                answersByPhase[q.phase][q.id] = a.answer;
            }

            const asrsAnswers = submittedAnswers
                .filter(a => allQuestions[a.questionIndex]?.phase === 'asrs')
                .map(a => ({ questionId: allQuestions[a.questionIndex].id, question: a.question, answer: a.answer }));

            const impairmentAnswers = submittedAnswers
                .filter(a => allQuestions[a.questionIndex]?.phase === 'impairment')
                .map(a => ({ questionId: allQuestions[a.questionIndex].id, question: a.question, answer: a.answer }));

            const personal = answersByPhase.personal || {};
            const prev = answersByPhase.previousAssessment || {};

            const payload = {
                asrsAnswers,
                impairmentAnswers,
                personalDetails: {
                    firstName: personal.names?.firstName || '',
                    middleName: personal.names?.middleName || '',
                    lastName: personal.names?.lastName || '',
                    dateOfBirth: personal.dob || '',
                    nhsNumber: personal.nhsNumber || '',
                    address: personal.address || {},
                    phone: personal.phone?.normalized || '',
                    email: personal.emailConfirm?.email || '',
                    
                },
                previousAssessment: {
                    assessedBefore: prev.assessedBefore?.value === 'Yes' || prev.assessedBefore === 'Yes',
                    existingDiagnosis: prev.existingDiagnosis?.value === 'Yes' || prev.existingDiagnosis === 'Yes',
                    previousMedication: prev.previousMedication?.value === 'Yes' || prev.previousMedication === 'Yes',
                    medicationDetails: prev.previousMedication?.details || '',
                },
                consentGiven: true,
                organisation: 'Well Life Clinic',
                source,
                sessionId: getOrCreateSessionId(),
            };

            const submitFn = httpsCallable(functions, 'submitWlcReferral');
            await submitFn(payload);

            Object.values(STORAGE_KEYS).forEach(k => localStorage.removeItem(k));
            setResultBannerType('success');
            setResultBannerMessage('Done! Check your inbox for your GP letter. Print it out or forward it to your GP surgery and ask for a Right to Choose referral to Well Life Clinic.');
            setShowResultBanner(true);
        } catch (error) {
            console.error('Error submitting WLC referral:', error);
            fireFunnelEvent({
                eventName: 'submission_failed',
                source,
                once: false,
            });
            setResultBannerType('error');
            setResultBannerMessage(
                'We weren\'t able to send your GP letter this time - this is usually a temporary issue on our end, not anything you\'ve done wrong. ' +
                'Please tap "Try Again" below.'
            );
            setShowResultBanner(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Shared header ───────────────────────────────────────────────────────
    const Header = () => (
        <div className="flex items-center justify-between p-4 z-10">
            {!isProd ? (
                <button onClick={clearStoredData} className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200" title="Clear all stored data (for testing)">Reset</button>
            ) : <div />}
            <div className="flex items-center space-x-3">
                <img src="/wlc_logo.png" alt="Well Life Clinic" className="h-5 w-auto" />
            </div>
        </div>
    );

    // =====================================================================
    // RENDER: Welcome Carousel
    // =====================================================================
    if (!welcomeComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                {!isProd && (
                    <div className="p-4 z-10">
                        <button onClick={clearStoredData} className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors duration-200" title="Clear all stored data (for testing)">Reset</button>
                    </div>
                )}
                <div className="flex-1 flex flex-col items-center justify-center px-6 pb-16 -mt-10">
                    <div className="w-full max-w-lg relative">                        

                        {/* Swipeable carousel container — tracks touch gestures on mobile */}
                        <div
                            onTouchStart={(e) => { e.currentTarget._touchStartX = e.touches[0].clientX; }}
                            onTouchEnd={(e) => {
                                const startX = e.currentTarget._touchStartX;
                                if (startX == null) return;
                                const delta = e.changedTouches[0].clientX - startX;
                                if (delta > 50 && currentWelcomeStep > 0) setCurrentWelcomeStep(currentWelcomeStep - 1);
                                else if (delta < -50 && currentWelcomeStep < 2) setCurrentWelcomeStep(currentWelcomeStep + 1);
                                e.currentTarget._touchStartX = null;
                            }}
                        >
                            {currentWelcomeStep === 0 && (
                                <div className="text-center space-y-5" style={{ animation: 'fade-up 0.3s ease-out' }}>
                                    <img src="/wlc_logo.png" alt="Well Life Clinic" className="h-16 mx-auto mb-4" />
                                    <h1 className="text-xl font-bold mb-3" style={{ color: BRAND.primary }}>
                                        Consultant-Led NHS ADHD Service
                                    </h1>
                                    <div className="space-y-3 text-left">
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            Do you find it hard to focus, stay organised, or keep on top of things? You might have ADHD - and support is available.
                                        </p>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            This questionnaire uses a clinically validated screening tool to help identify whether ADHD symptoms might be affecting you. It takes about <span className="font-semibold">5 minutes</span>.
                                        </p>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            At the end, you'll get a personalised result and, if relevant, we'll email you a letter to send to your GP requesting an NHS-funded referral through Well Life Clinic.
                                        </p>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            Your responses are kept <span className="font-semibold">strictly confidential</span> and <span className="font-semibold">never shared without your consent</span>.
                                        </p>
                                    </div>
                                </div>
                            )}
                            {currentWelcomeStep === 1 && (
                                <div className="text-center space-y-5" style={{ animation: 'fade-up 0.3s ease-out' }}>
                                    <img src="/wlc_logo.png" alt="Well Life Clinic" className="h-16 mx-auto mb-4" />
                                    <h2 className="text-lg font-semibold" style={{ color: BRAND.primary }}>What is Right to Choose?</h2>
                                    <div className="space-y-3 text-left">
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            If you are aged 18 or over and registered with a GP in England, you have a <span className="font-semibold">legal right to choose</span> your preferred NHS-approved provider for your ADHD assessment.
                                        </p>
                                        <p className="text-sm text-gray-700 leading-relaxed">
                                            Your GP initiates the referral. The assessment is <span className="font-semibold">funded by the NHS</span>, so there is no cost to you.
                                        </p>
                                    </div>
                                </div>
                            )}
                            {currentWelcomeStep === 2 && (
                                <div className="text-center space-y-5" style={{ animation: 'fade-up 0.3s ease-out' }}>
                                    <img src="/wlc_logo.png" alt="Well Life Clinic" className="h-16 mx-auto mb-4" />
                                    <h2 className="text-lg font-semibold" style={{ color: BRAND.primary }}>Why Well Life Clinic?</h2>
                                    <div className="space-y-2.5 text-left">
                                        <div className="flex items-start gap-2.5">
                                            <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                                            <p className="text-sm text-gray-700 leading-relaxed">One of the <span className="font-semibold">shortest wait times</span> in the UK</p>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                                            <p className="text-sm text-gray-700 leading-relaxed">Every case discussed with an <span className="font-semibold">expert consultant psychiatrist</span></p>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                                            <p className="text-sm text-gray-700 leading-relaxed"><span className="font-semibold">Complete ADHD service</span> - diagnosis, medication, shared care agreements and ongoing reviews</p>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span>
                                            <p className="text-sm text-gray-700 leading-relaxed"><span className="font-semibold">Virtual appointments</span> - private, convenient and available nationwide</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Dot indicators — clickable to jump to any step */}
                        <div className="flex justify-center mt-6 space-x-2">
                            {[0, 1, 2].map((step) => (
                                <button
                                    key={step}
                                    onClick={() => setCurrentWelcomeStep(step)}
                                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${step === currentWelcomeStep ? 'scale-125' : 'opacity-50 hover:opacity-75'}`}
                                    style={{ backgroundColor: step === currentWelcomeStep ? BRAND.accent : BRAND.primary }}
                                    aria-label={`Go to slide ${step + 1}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="fixed bottom-0 left-0 right-0 backdrop-blur-sm p-4 max-w-lg mx-auto w-full">
                    <button
                        onClick={() => {
                            // Leaving the current welcome slide — record completion
                            // before advancing so swipe/dot jumps still get a viewed
                            // event for the destination via the step_viewed effect.
                            const leaving = getWelcomeStepMeta(currentWelcomeStep);
                            fireFunnelEvent({
                                eventName: 'step_completed',
                                source,
                                once: true,
                                ...leaving,
                            });

                            if (currentWelcomeStep < 2) { setCurrentWelcomeStep(currentWelcomeStep + 1); }
                            else {
                                setWelcomeComplete(true);
                                localStorage.setItem(STORAGE_KEYS.WELCOME_COMPLETE, 'true');
                                // Fire form_start event only on genuinely fresh attempts (no
                                // existing progress in localStorage). This prevents a re-fire
                                // if the user navigates back to the carousel and re-advances.
                                const hasExistingProgress = localStorage.getItem(STORAGE_KEYS.SUBMITTED_ANSWERS);
                                if (!hasExistingProgress || hasExistingProgress === '[]') {
                                    fireEventOnce('form_start', source, STORAGE_KEYS.FORM_START_LOGGED);
                                }
                            }
                        }}
                        className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105"
                        style={{ backgroundColor: BRAND.primary, color: 'white' }}
                    >
                        {currentWelcomeStep < 2 ? 'Next' : 'Start'}
                    </button>
                </div>
                <style>{ANIMATIONS}</style>
            </div>
        );
    }

    // =====================================================================
    // RENDER: Break screen after ASRS Part A (above threshold)
    // =====================================================================
    if (showScoreMessage) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        <div className="p-8 text-left" style={{ animation: 'fade-up 0.8s ease-out 0.7s both' }}>
                            <div className="space-y-4">
                                <div className="text-gray-700 leading-relaxed" style={{ animation: 'fade-slide-in 0.3s ease-out 0.6s both' }}>
                                    <p>Your initial responses suggest <span className="font-semibold">you may have ADHD</span>.</p>
                                </div>
                                <div className="text-gray-700 leading-relaxed" style={{ animation: 'fade-slide-in 0.3s ease-out 0.9s both' }}>
                                    <p>To build a fuller picture, please answer a few more questions about how these difficulties affect your daily life.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="fixed bottom-0 left-0 right-0 backdrop-blur-sm p-4 max-w-lg mx-auto w-full">
                    <button onClick={handleScoreMessageContinue} className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 hover:scale-105" style={{ backgroundColor: BRAND.primary, color: 'white' }}>Continue</button>
                </div>
                <style>{ANIMATIONS}</style>
            </div>
        );
    }

    // =====================================================================
    // RENDER: Below-threshold end screen
    // =====================================================================
    if (belowThreshold) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        <div className="mb-8">
                            <CheckCircleIcon className="w-28 h-28 mx-auto" style={{ color: BRAND.accent, animation: 'fade-in 1s ease-out' }} />
                        </div>
                        <h1 className="text-2xl font-bold mb-8 tracking-tight" style={{ color: BRAND.primary, animation: 'fade-up 0.8s ease-out 0.5s both' }}>Screening Results</h1>
                        <div className="p-8 text-left" style={{ animation: 'fade-up 0.8s ease-out 0.7s both' }}>
                            <div className="text-sm space-y-4">
                                <p className="text-gray-700 leading-relaxed"><span className="font-semibold">Your screening results suggest ADHD is less likely.</span> Your responses didn't reach the threshold we typically need to support a Right to Choose referral.</p>
                                <p className="text-gray-700 leading-relaxed">However, you're very welcome to get in touch. Well Life Clinic provides a complete ADHD service and may still be able to help.</p>
                                <ul className="text-gray-700 leading-relaxed ml-4 space-y-1">
                                    <li>Email: <span className="font-semibold">rtcadhd@welllife-clinic.co.uk</span></li>
                                    <li>Phone: <span className="font-semibold">01883 338336</span></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <style>{ANIMATIONS}</style>
            </div>
        );
    }

    // =====================================================================
    // RENDER: Completion screen with consent + submit
    // =====================================================================
    if (isComplete) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center px-4">
                    <div className="max-w-lg w-full text-center">
                        <div className="mb-8">
                            {showResultBanner && resultBannerType === 'error' ? (
                                <svg className="w-28 h-28 mx-auto text-red-400" style={{ animation: 'fade-in 1s ease-out' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            ) : (
                                <CheckCircleIcon className="w-28 h-28 mx-auto" style={{ color: BRAND.accent, animation: 'fade-in 1s ease-out' }} />
                            )}
                        </div>
                        <h1 className="text-3xl font-bold mb-6 tracking-tight" style={{ color: BRAND.primary, animation: 'fade-up 0.8s ease-out 0.5s both' }}>
                            {showResultBanner && resultBannerType === 'success'
                                ? 'Form Complete'
                                : showResultBanner && resultBannerType === 'error'
                                    ? "You're almost done"
                                    : "You're almost done"}
                        </h1>

                        {/* Error banner — shown above the retry button so the patient
                            reads the explanation first, then sees the action they can take. */}
                        {showResultBanner && resultBannerType === 'error' && (
                            <div className="mx-6 mb-4 p-4 rounded-lg border bg-red-50 border-red-200 text-left" style={{ animation: 'fade-slide-up 0.5s ease-out' }}>
                                <div className="flex items-start">
                                    <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                    <p className="text-sm text-red-800 leading-relaxed">{resultBannerMessage}</p>
                                </div>
                            </div>
                        )}

                        {!(showResultBanner && resultBannerType === 'success') && (
                            <>
                                {/* Pre-submission instructions — hidden after an error since the
                                    error banner provides its own guidance and repeating these
                                    instructions would be confusing. */}
                                {!(showResultBanner && resultBannerType === 'error') && (
                                    <div className="p-6 text-left" style={{ animation: 'fade-up 0.8s ease-out 0.7s both' }}>
                                        <div className="text-sm space-y-4">
                                            <p className="text-gray-700 leading-relaxed">When you press <span className="font-semibold">'Submit'</span> below, we will email you a <span className="font-semibold">letter for your GP</span>. Print it out or forward the email, then ask your GP for a referral to Well Life Clinic under <span className="font-semibold">Right to Choose</span>.</p>
                                            <p className="text-gray-700 leading-relaxed">Well Life Clinic will also be notified so they can support you through the referral process.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Consent checkbox — already ticked from original submission,
                                    kept visible so the patient doesn't need to re-check it. */}
                                <div className="px-6 mb-6 text-left" style={{ animation: 'fade-up 0.8s ease-out 1.0s both' }}>
                                    <label className="flex items-start gap-3 cursor-pointer group">
                                        <input
                                            type="checkbox"
                                            checked={consentGiven}
                                            onChange={(e) => setConsentGiven(e.target.checked)}
                                            className="mt-1 h-4 w-4 rounded border-gray-300 focus:ring-2"
                                            style={{ accentColor: BRAND.primary }}
                                        />
                                        <span className="text-sm text-gray-700 leading-relaxed">
                                            I consent to Well Life Clinic contacting me to support me in seeking a Right to Choose referral.
                                        </span>
                                    </label>
                                </div>

                                {/* Submit / retry button */}
                                <div className="px-6" style={{ animation: 'fade-up 0.8s ease-out 1.3s both' }}>
                                    <button
                                        onClick={handleFinalSubmit}
                                        disabled={isSubmitting || !consentGiven}
                                        className={`w-full font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center ${
                                            isSubmitting || !consentGiven
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
                                                : 'hover:scale-105 shadow-lg hover:shadow-xl'
                                        }`}
                                        style={{
                                            backgroundColor: (isSubmitting || !consentGiven) ? undefined : BRAND.primary,
                                            color: (isSubmitting || !consentGiven) ? undefined : 'white',
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                                Submitting...
                                            </>
                                        ) : (showResultBanner && resultBannerType === 'error') ? 'Try Again' : 'Submit'}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Success banner — shown after the form is hidden */}
                        {showResultBanner && resultBannerType === 'success' && (
                            <div className="mx-6 mt-6 p-4 rounded-lg border bg-green-50 border-green-200" style={{ animation: 'fade-slide-up 0.5s ease-out' }}>
                                <div className="flex items-center">
                                    <svg className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    <p className="text-sm font-medium text-green-800">{resultBannerMessage}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <style>{ANIMATIONS}</style>
            </div>
        );
    }

    // =====================================================================
    // RENDER: Question-by-question form
    // =====================================================================
    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-yellow-50 flex flex-col">
            <Header />
            <div className="flex-1 overflow-y-auto px-4 pb-20 pt-2">
                <div className="max-w-lg mx-auto w-full">
                    <div className="mb-4">
                        <form onSubmit={handleSubmit(onSubmit)} className="p-4">
                            {/* Question title */}
                            <h2 className="text-base font-bold text-gray-900 mb-1 leading-snug text-left">{currentQuestion.question}</h2>
                            {currentQuestion.subtitle && <p className="text-sm text-gray-500 mb-3 text-left italic">{currentQuestion.subtitle}</p>}
                            {currentQuestion.instruction && !currentQuestion.subtitle && <p className="text-gray-600 text-sm mb-3 text-left">{currentQuestion.instruction}</p>}

                            {/* ── Rating (ASRS) ──────────────────────────── */}
                            {currentQuestion.type === 'rating' && (
                                <div className="mb-4">
                                    <p className="text-gray-500 text-sm mb-2 italic text-left">Please select one.</p>
                                    <div className="space-y-1.5">
                                        {ASRS_RATING_OPTIONS.map((opt) => {
                                            const isSelected = watch(currentQuestion.id) === opt;
                                            return (
                                                <label key={opt} className="flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-300 ease-out" style={{ transform: isSelected ? 'scale(1.05)' : 'scale(1)', backgroundColor: isSelected ? '#fef7e0' : '', borderColor: isSelected ? BRAND.accent : '#e5e7eb' }}
                                                    onClick={(e) => { if (isSelected) { e.preventDefault(); setValue(currentQuestion.id, ''); } }}>
                                                    <div className="flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-300" style={{ borderColor: isSelected ? BRAND.accent : '#d1d5db', backgroundColor: isSelected ? BRAND.accent : 'transparent' }}>
                                                        {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                                    </div>
                                                    <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{opt}</span>
                                                    <input type="radio" value={opt} className="hidden" {...register(currentQuestion.id)} />
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Likert (Impairment) ────────────────────── */}
                            {currentQuestion.type === 'likert' && (
                                <div className="mb-4">
                                    <div className="space-y-1.5">
                                        {IMPAIRMENT_LIKERT_OPTIONS.map((opt) => {
                                            const isSelected = watch(currentQuestion.id) === opt.value;
                                            return (
                                                <label key={opt.value} className="flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-300 ease-out" style={{ transform: isSelected ? 'scale(1.05)' : 'scale(1)', backgroundColor: isSelected ? '#fef7e0' : '', borderColor: isSelected ? BRAND.accent : '#e5e7eb' }}
                                                    onClick={(e) => { if (isSelected) { e.preventDefault(); setValue(currentQuestion.id, ''); } }}>
                                                    <div className="flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-300" style={{ borderColor: isSelected ? BRAND.accent : '#d1d5db', backgroundColor: isSelected ? BRAND.accent : 'transparent' }}>
                                                        {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                                    </div>
                                                    <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{opt.label}</span>
                                                    <input type="radio" value={opt.value} className="hidden" {...register(currentQuestion.id)} />
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Names ───────────────────────────────────── */}
                            {currentQuestion.type === 'names' && (
                                <div className="mb-4 space-y-3">
                                    {currentQuestion.fields.map((field) => (
                                        <div key={field.id}>
                                            <label className="block text-sm font-medium text-gray-700 mb-1 text-left">{field.label}</label>
                                            <input type="text" className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200" style={{ '--tw-ring-color': BRAND.accent }} placeholder={`Enter your ${field.label.toLowerCase().replace(' (optional)', '')}`} {...register(field.id)} aria-label={field.label} />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ── Date of Birth ──────────────────────────── */}
                            {currentQuestion.type === 'dob' && (
                                <div className="mb-4">
                                    <WlcDateOfBirthPicker value={watch('dob') || ''} onChange={(val) => setValue('dob', val, { shouldValidate: true, shouldDirty: true })} minAgeYears={17} maxAgeYears={120} />
                                    <input type="hidden" value={watch('dob') || ''} {...register('dob')} />
                                </div>
                            )}

                            {/* ── NHS Number ─────────────────────────────── */}
                            {currentQuestion.type === 'nhsNumber' && (
                                <div className="mb-4">
                                    {currentQuestion.nudgeText && <p className="text-gray-500 text-xs mb-2 italic text-left">{currentQuestion.nudgeText}</p>}
                                    <input type="text" inputMode="numeric" className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200" style={{ '--tw-ring-color': BRAND.accent }} placeholder="e.g. 943 476 5919" maxLength={12} {...register('nhsNumber')} />
                                </div>
                            )}

                            {/* ── Address ─────────────────────────────────── */}
                            {currentQuestion.type === 'address' && (
                                <div className="mb-4">
                                    <WlcAddressField value={addressValue} onChange={setAddressValue} />
                                </div>
                            )}

                            {/* ── Phone ───────────────────────────────────── */}
                            {currentQuestion.type === 'phone' && (
                                <div className="mb-4">
                                    <WlcPhoneNumberField rawValue={phoneRaw} onRawChange={setPhoneRaw} onNormalizedChange={setPhoneNormalized} label={null} required />
                                </div>
                            )}

                            {/* ── Email + Confirm ─────────────────────────── */}
                            {currentQuestion.type === 'emailConfirm' && (
                                <div className="mb-4 space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Email address</label>
                                        <input type="email" className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200" style={{ '--tw-ring-color': BRAND.accent }} placeholder="Enter your email address" {...register('email')} />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1 text-left">Confirm email address</label>
                                        <input type="email" className="w-full p-3 border border-gray-200 rounded-xl bg-white text-gray-900 text-base placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent transition-all duration-200" style={{ '--tw-ring-color': BRAND.accent }} placeholder="Re-enter your email address" {...register('confirmEmail')} />
                                    </div>
                                </div>
                            )}

                            {/* ── Yes / No ────────────────────────────────── */}
                            {currentQuestion.type === 'yesNo' && (
                                <div className="mb-4">
                                    {/* Hidden input keeps react-hook-form aware of the field so
                                        handleSubmit includes it in `data`. Selection is fully
                                        controlled via setValue — no native radio behaviour. */}
                                    <input type="hidden" {...register(currentQuestion.id)} />
                                    <div className="space-y-1.5">
                                        {YES_NO_OPTIONS.map((opt) => {
                                            const isSelected = watch(currentQuestion.id) === opt;
                                            const showConditional = isSelected && opt === currentQuestion.conditionalTrigger && (currentQuestion.conditionalInfo || currentQuestion.conditionalInput);
                                            return (
                                                <div key={opt} className="transition-all duration-300 ease-out origin-center"
                                                    style={{ transform: isSelected ? 'scale(1.05)' : 'scale(1)' }}>
                                                    <div className="flex items-center p-2.5 rounded-lg border-2 cursor-pointer transition-all duration-300 ease-out"
                                                        role="radio" aria-checked={isSelected} tabIndex={0}
                                                        style={{ backgroundColor: isSelected ? '#fef7e0' : '', borderColor: isSelected ? BRAND.accent : '#e5e7eb', borderBottomLeftRadius: showConditional ? 0 : undefined, borderBottomRightRadius: showConditional ? 0 : undefined }}
                                                        onClick={() => setValue(currentQuestion.id, isSelected ? '' : opt)}>
                                                        <div className="flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-300" style={{ borderColor: isSelected ? BRAND.accent : '#d1d5db', backgroundColor: isSelected ? BRAND.accent : 'transparent' }}>
                                                            {isSelected && <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                                        </div>
                                                        <span className={`text-sm ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{opt}</span>
                                                    </div>
                                                    {/* Only render the conditional panel for the triggering option
                                                       (e.g. "Yes") so the textarea DOM element exists once, avoiding
                                                       a duplicate-register bug with react-hook-form. */}
                                                    {opt === currentQuestion.conditionalTrigger && (currentQuestion.conditionalInfo || currentQuestion.conditionalInput) && (
                                                        <div className={`transition-all duration-300 overflow-hidden ${showConditional ? 'max-h-48' : 'max-h-0'}`}>
                                                            <div className="p-3 bg-blue-50 border-2 border-t-0 rounded-b-lg" style={{ borderColor: BRAND.accent }}>
                                                                {currentQuestion.conditionalInfo && <p className="text-sm text-blue-800">{currentQuestion.conditionalInfo}</p>}
                                                                {currentQuestion.conditionalInput && (
                                                                    <textarea
                                                                        className="w-full mt-2 p-3 border border-gray-200 rounded-lg bg-white text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none"
                                                                        style={{ '--tw-ring-color': BRAND.accent }}
                                                                        rows={3}
                                                                        placeholder={currentQuestion.conditionalInput.placeholder}
                                                                        {...register('medicationDetails')}
                                                                    />
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* ── Inline validation error ─────────────────── */}
                            {inlineError && <p className="text-red-500 text-sm mb-3 text-left">{inlineError}</p>}

                            {/* ── Navigation buttons ─────────────────────── */}
                            <div className="flex items-center justify-between mt-2">
                                <button type="button" onClick={handlePrevious}
                                    className="text-sm font-medium transition-all duration-200 hover:scale-105"
                                    style={{ color: BRAND.primary }}>
                                    ← Back
                                </button>
                                <button type="submit"
                                    className="text-white font-semibold py-2 px-5 rounded-xl transition-all duration-200 text-sm hover:scale-105 transform"
                                    style={{ backgroundColor: BRAND.primary }}
                                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#2f1829'; }}
                                    onMouseLeave={(e) => { e.target.style.backgroundColor = BRAND.primary; }}>
                                    Next →
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Sticky progress footer with segmented bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3">
                <div className="max-w-lg mx-auto">
                    <p className="text-gray-500 text-xs font-medium text-center mb-2">
                        {remainingMinutes <= 1 ? 'Almost done!' : `About ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''} remaining`}
                    </p>
                    <div className="flex gap-1.5">
                        <div className="flex-[6] bg-gray-200 rounded-full h-2 overflow-hidden" title="Screening questions">
                            <div className="h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${asrsProgress}%`, backgroundColor: BRAND.accent }} />
                        </div>
                        <div className="flex-[15] bg-gray-200 rounded-full h-2 overflow-hidden" title="Details &amp; history">
                            <div className="h-2 rounded-full transition-all duration-500 ease-out" style={{ width: `${postAsrsProgress}%`, backgroundColor: BRAND.accent }} />
                        </div>
                    </div>
                </div>
            </div>
            <style>{ANIMATIONS}</style>
        </div>
    );
}
